/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2019 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import AudioManager from '../../Audio/AudioManager';
import DtModObservable from '../../Game/DtModObservable';
import LobbyAction from '../../Playbar/LobbyAction';
import { ccEnum } from '../CCEnum';
import CLog, { LogTypes } from '../ComponentLogger';
import { AnyFunc } from '../Types';
import InjectableComponent from './ComponentReference/InjectableComponent';
import KeyShortcutComponent from './KeyShortcutComponent';
import OptionalComponent from './Optional/OptionalComponent';
import PopupDispatcher from './Popup/PopupDispatcher';
import ShortcutEvent from './ShortcutEvent';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

@cc._decorator.ccclass
export default class DirectorShortcutComponent extends KeyShortcutComponent
{
    /** Keyboard key. */
    @property({ type: ccEnum.ccKEY, visible: true, override: true })
    protected _key: cc.KEY = cc.KEY.p;

    @property(cc.Float)
    protected step: number = .05;

    @property(cc.Float)
    protected minDtMod: number = .05;

    private _shortcutEvent: ShortcutEvent = null;

    private _dtModObservable: InjectableComponent<DtModObservable> = new InjectableComponent();
    private _audioManagerRef: InjectableComponent<AudioManager> = new InjectableComponent();
    private _componentEnabledMap: Map<cc.Component, boolean> = new Map();

    private static readonly InteractableComponentTypes: { new(): cc.Component }[] =
    [
        cc.Button,
        PopupDispatcher,
        LobbyAction
    ];

    /** @hidden */
    protected setContexts(): void
    {
        super.setContexts();
        this._dtModObservable.SetContext(DtModObservable);
        this._audioManagerRef.SetContext(AudioManager);
    }

    /** @hidden */
    protected onLoad(): void
    {
        super.onLoad();
        this._shortcutEvent = this.addComponent(ShortcutEvent);
        this._shortcutEvent.KeyShortcutComponent = this;
        this._shortcutEvent.OnShortcut(() => this.execute());

        this.addShortcutEvent(cc.KEY["+"], () => this.safeMutateDtMod(this.step));
        this.addShortcutEvent(cc.KEY["-"], () => this.safeMutateDtMod(-this.step));
        this.addShortcutEvent(cc.KEY.num0, () =>
            {
                this._dtModObservable.WithValue(dtMod => dtMod.Reset());
                CLog.log(`SETTING DT MOD: 1.0`, LogTypes.Game);
            });

        OptionalComponent.AddDemoOptionalComponent(this);

        super.onLoad();
    }

    private safeMutateDtMod(value: number): void
    {
        this._dtModObservable.WithValue(dtMod =>
            {
                const maxValue: number = dtMod.MaxValue > 0 ? dtMod.MaxValue : 1000;
                let newValue: number = dtMod.Value + value;
                if (newValue < this.minDtMod) { newValue = this.minDtMod; }
                else if (newValue > maxValue) { newValue = maxValue; }

                newValue = Math.round(newValue * 100) / 100.0;

                dtMod.Value = newValue;
                CLog.log(`SETTING DT MOD: ${newValue}`, LogTypes.Game);
            });
    }

    private addShortcutEvent(key: cc.KEY, fnOnShortCut: AnyFunc): void
    {
        const child: cc.Node = new cc.Node(`ShortCutevent.${key.toString()}`);
        this.node.addChild(child);

        const shortcut: KeyShortcutComponent = child.addComponent(KeyShortcutComponent);
        shortcut.Key = key;

        const shortcutEvent: ShortcutEvent = child.addComponent(ShortcutEvent);
        shortcutEvent.KeyShortcutComponent = shortcut;
        shortcutEvent.OnShortcut(fnOnShortCut);
    }

    /** @hidden */
    protected onDestroy(): void
    {
        super.onDestroy();
        this._shortcutEvent.OffShortcut(() => this.execute());
    }

    private execute(): void
    {
        const canvas: cc.Canvas = cc.Canvas.instance;
        if (cc.director.isPaused())
        {
            cc.director.resume();
            this._audioManagerRef.WithValue(audioManager => audioManager.Resume());

            DirectorShortcutComponent.InteractableComponentTypes.forEach(componentType =>
                {
                    canvas.getComponentsInChildren(componentType).forEach(comp => this.resetHandler(comp));
                });
        }
        else
        {
            cc.director.pause();
            this._audioManagerRef.WithValue(audioManager => audioManager.Pause());

            DirectorShortcutComponent.InteractableComponentTypes.forEach(componentType =>
                {
                    canvas.getComponentsInChildren(componentType).forEach(comp => this.disableHandler(comp));
                });
        }
    }

    private disableHandler(component: cc.Component): void
    {
        this._componentEnabledMap.set(component, component.enabled);
        component.enabled = false;
    }

    private resetHandler(component: cc.Component): void
    {
        let enabled: boolean = this._componentEnabledMap.get(component);
        if (enabled !== true) { enabled = false; }
        component.enabled = enabled;
    }
}
